import { Loader2, LockKeyhole, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RangeEditor } from "@/components/charts/RangeEditor";
import { ChartViewControl, OpenEditorButton } from "@/components/charts/ChartToolbar";
import { ChartModal, ChartPreview, useModalChartHeight } from "@/components/charts/ChartModal";
import { padded } from "@/api/adapt";
import type { NodeResultsDto } from "@/api/types";
import { useMetricStated } from "@/hooks/stated";
import { useActiveScenario, useNodeDetail, useNodeResults } from "@/hooks/world";
import { fmtInput, fmtValue, parseAmount } from "@/lib/format";
import { addMonths, fmtMonth, idx, N, NOW, sortMonths } from "@/lib/months";
import { heroDate } from "@/lib/results";
import { cn } from "@/lib/utils";
import { ancestors, drivers } from "@/model/graph";
import { useCanEdit } from "@/model/access";
import { useStore, useTerm } from "@/model/store";
import type { MetricNode } from "@/model/types";
import { HERO_NUMBER, HeroDateSelect, Stat, usePreviewHeight } from "./Hero";
import { NodeHeader } from "./NodeHeader";
import { NodeScenarioStrip, ScenarioBar } from "@/components/scenarios/Scenarios";
import { AdminGroup, HistoryPanel, LinksPanel, NextStepCallout, Section, useIsolated } from "./NodeSide";
import { WhatMattersMost } from "./WhatMattersMost";
import { NodeIcon } from "@/components/NodeIcon";

type Range = Record<string, [number, number, number]>;
type Q = [number[], number[], number[], number[], number[]];

/** p10…p90 of a node run from the API, on the chart grid. */
function quantiles(run: NodeResultsDto["model"] | null | undefined): Q | null {
	const s = run?.series;
	if (!s || !("p50" in s)) return null;
	return [padded(s.p10), padded(s.p25), padded(s.p50), padded(s.p75), padded(s.p90)];
}

export function MetricView({ node }: { node: MetricNode }) {
	const term = useTerm();
	const analyst = useStore((s) => s.analyst);
	const nodes = useStore((s) => s.world.nodes);
	const links = useStore((s) => s.world.links);
	const saveRange = useStore((s) => s.saveRange);
	const saveFormula = useStore((s) => s.saveFormula);
	const checkFormula = useStore((s) => s.checkFormula);
	const addObservation = useStore((s) => s.addObservation);
	const removeObservation = useStore((s) => s.removeObservation);
	const { scenario } = useActiveScenario();
	const upsertLever = useStore((s) => s.upsertLever);
	const res = useNodeResults(node.id, scenario?.id);
	const detail = useNodeDetail(node.id, "year", scenario?.id ?? null);
	const isolated = useIsolated(node);
	const previewHeight = usePreviewHeight();

	const [draft, setDraft] = useState<Range | null>(null);
	// "Fit" (default) frames the chart to the data: from the first observation to just past the last entered date.
	const [view, setView] = useState<string>("fit");
	const [hero, setHero] = useState(heroDate(node));
	const [formula, setFormula] = useState<string | null>(null);
	const lastObs = node.observed.at(-1)?.month ?? NOW;
	const [obsMonth, setObsMonth] = useState(addMonths(lastObs, 1));
	const [obsValue, setObsValue] = useState("");
	const [newMonth, setNewMonth] = useState("");
	const [editing, setEditing] = useState(false);
	const modalHeight = useModalChartHeight();

	// In a scenario the chart puts the scenario's result forward. The chart and table edit the scenario's own range
	// for this metric once it has one ("Give it its own values"); until then they show the baseline's, fixed.
	const lever = scenario?.levers.find((l) => l.node === node.id);
	const inScenario = !!scenario;
	const ownValues = lever?.kind === "range";
	const baselineFixed = inScenario && !ownValues;
	const activeRange = lever?.kind === "range" ? lever.range : node.range;
	const range = draft ?? activeRange;
	const ancestorIds = useMemo(() => [...ancestors(node.id, nodes, links)], [node.id, nodes, links]);
	function saveActive(r: Record<string, [number, number, number]>) {
		if (scenario && !node.formula) void upsertLever(scenario.id, { id: node.id, kind: "range", node: node.id, range: r });
		else void saveRange(node.id, r);
	}
	// Calculated metrics show their formula over past months where every input has an actual (from the API).
	const formulaHist = detail.data?.formula_history;
	const history = useMemo(() => (node.formula ? (formulaHist ?? []) : node.observed), [node.formula, node.observed, formulaHist]);
	const shown = useMemo(() => (node.formula ? { ...node, observed: history } : node), [node, history]);
	const fit = useMemo(() => {
		// A calculated metric fits to the dates of the metrics it is built from.
		const sources = node.formula
			? [...ancestors(node.id, nodes, links)].map((id) => nodes[id]).filter((n): n is MetricNode => n?.kind === "metric")
			: [{ ...node, range }];
		const months = [...sources.flatMap((n) => [...Object.keys(n.range), ...n.observed.map((o) => o.month)]), ...history.map((o) => o.month)];
		const ts = months.map(idx).filter((t) => t < N);
		const last = Math.max(0, ...ts);
		const end = Math.min(N - 1, last + Math.max(1, Math.round((last + 1) * 0.15)));
		const first = Math.min(-1, ...ts);
		return { start: Math.max(-12, first - 1), end };
		// Recompute when dates are added or removed, not on every drag.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [Object.keys(node.range).join(), node.observed.length, node.formula, history.length, nodes, links]);
	const viewEnd = view === "fit" ? fit.end : Number(view);
	const viewStart = view === "fit" ? fit.start : -12;
	const isFormula = !!node.formula;
	const canEdit = useCanEdit();
	const isConstant = !isFormula && Object.values(activeRange).every(([a, b, c]) => a === b && b === c);
	const labels = analyst ? { low: "p10", likely: "p50", high: "p90" } : { low: "Low", likely: "Likely", high: "High" };

	// "With risks" is the scenario's result while one is selected; "Without scenario" is Baseline's.
	const implied = useMemo(() => quantiles(res.data?.model), [res.data]);
	const baseQ = useMemo(
		() => (res.data ? quantiles(inScenario ? (res.data.without_scenario ?? res.data.model) : res.data.model) : null),
		[res.data, inScenario],
	);
	const pending = res.pending;
	// The stated bands: the API's exact series, or a straight-line preview while dragging.
	const plan = useMetricStated(node, range, draft !== null, scenario?.id ?? null);
	const ht = Math.min(N - 1, Math.max(0, idx(hero)));
	const u = node.unit;
	// The formula is checked by the API as it is typed (`POST /nodes/:h/formula/check`), the same check as the save.
	const [check, setCheck] = useState<{ formula: string; error: string | null } | null>(null);
	useEffect(() => {
		if (formula === null || formula === node.formula) return;
		let alive = true;
		const t = setTimeout(() => {
			void checkFormula(node.id, formula).then((r) => {
				if (!alive || !r) return;
				const error = r.error ?? (r.unknown.length ? `Unknown: ${r.unknown.join(", ")}.` : null) ?? r.issues[0]?.message ?? null;
				setCheck({ formula, error });
			});
		}, 250);
		return () => {
			alive = false;
			clearTimeout(t);
		};
	}, [formula, node.id, node.formula, checkFormula]);
	const fErr = formula !== null && formula !== node.formula ? (check?.formula === formula ? check.error : null) : (res.data?.model.error ?? null);
	const checking = formula !== null && formula !== node.formula && check?.formula !== formula;
	const obsParsed = parseAmount(obsValue, u);
	const hasDrivers = drivers(node.id, nodes, links).length > 0;
	const keyDates = sortMonths([...new Set([...node.key_dates, ...Object.keys(activeRange)])]).filter((m) => idx(m) >= 0);

	function addDate() {
		if (!newMonth || !plan || idx(newMonth) < 0 || idx(newMonth) >= N || newMonth in activeRange) return;
		const t = idx(newMonth);
		saveActive({ ...activeRange, [newMonth]: [plan.lo[t], plan.mid[t], plan.hi[t]] });
		setNewMonth("");
	}

	const tableLocked = baselineFixed || !canEdit;
	const rangeTable = () => (
		<div className="overflow-x-auto">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b text-left text-muted-foreground">
						<th className="py-2 pr-4 font-normal">{isConstant ? "Value from" : ownValues ? `${scenario!.name} by` : "Baseline by"}</th>
						{!isConstant && (
							<>
								<th className="px-3 py-2 text-right font-normal">{labels.low}</th>
								<th className="px-3 py-2 text-right font-normal">{labels.likely}</th>
								<th className="px-3 py-2 text-right font-normal">{labels.high}</th>
							</>
						)}
						{isConstant && <th className="px-3 py-2 text-right font-normal">Value</th>}
						<th className="px-3 py-2 text-right font-normal">{term("With risks", "p50")}</th>
						<th className="w-8" />
					</tr>
				</thead>
				<tbody className="tabular-nums">
					{sortMonths(Object.keys(activeRange)).map((m) => {
						const t = Math.min(N - 1, idx(m));
						const cell = (k: 0 | 1 | 2) =>
							tableLocked ? (
								<td key={k} className="px-3 py-1.5 text-right text-muted-foreground">
									{fmtValue(activeRange[m][k], u)}
								</td>
							) : (
								<td key={k} className="px-3 py-1.5 text-right">
									<Input
										key={`${m}-${k}-${activeRange[m][k]}`}
										className="ml-auto h-8 w-36 text-right tabular-nums"
										inputMode="decimal"
										defaultValue={fmtInput(activeRange[m][k], u)}
										aria-label={`${isConstant ? "Value" : [labels.low, labels.likely, labels.high][k]} from ${fmtMonth(m)}`}
										onFocus={(e) => e.target.select()}
										onBlur={(e) => {
											const v = parseAmount(e.target.value, u);
											if (v === null || !Number.isFinite(v)) {
												e.target.value = fmtInput(activeRange[m][k], u);
												return;
											}
											if (v === activeRange[m][k]) {
												e.target.value = fmtInput(v, u);
												return;
											}
											const r = [...activeRange[m]] as [number, number, number];
											if (isConstant) r[0] = r[1] = r[2] = v;
											else r[k] = v;
											r.sort((a, b) => a - b);
											saveActive({ ...activeRange, [m]: r });
										}}
										onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
									/>
								</td>
							);
						return (
							<tr key={m} className="border-b last:border-0">
								<td className="py-1.5 pr-4">{fmtMonth(m)}</td>
								{isConstant ? cell(1) : [cell(0), cell(1), cell(2)]}
								<td className="px-3 py-1.5 text-right text-[var(--model)]">{implied ? fmtValue(implied[2][t], u) : "…"}</td>
								<td className="py-1.5 pl-1 text-right">
									{!tableLocked && Object.keys(activeRange).length > 1 && (
										<Button
											variant="ghost"
											size="icon"
											className="size-7"
											title="Remove this date"
											aria-label={`Remove ${fmtMonth(m)}`}
											onClick={() => {
												const next = { ...activeRange };
												delete next[m];
												saveActive(next);
											}}
										>
											<X className="size-3.5" />
										</Button>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			{!tableLocked && (
				<>
					<div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
						<Input type="month" className="h-8 w-44" value={newMonth} onChange={(e) => setNewMonth(e.target.value)} aria-label="New date" />
						<Button size="sm" variant="outline" onClick={addDate} disabled={!newMonth || idx(newMonth) < 0 || idx(newMonth) >= N || newMonth in activeRange}>
							<Plus className="size-3.5" /> Add date
						</Button>
						<span className="text-xs text-muted-foreground">New dates start from the current baseline at that date.</span>
					</div>
					<p className="mt-1 text-xs text-muted-foreground">Type values the short way: 25B, $2.5M, 150k, 5%.</p>
				</>
			)}
		</div>
	);

	// The chart's legend is drawn under it by the chart itself (without "History" when there is none).
	const toolbar = (inModal: boolean) => (
		<div className="ml-auto flex flex-wrap items-center gap-2">
			{pending && (
				<span className="flex items-center gap-1 text-xs text-muted-foreground">
					<Loader2 className="size-3.5 animate-spin" /> Updating…
				</span>
			)}
			<ChartViewControl fit value={view === "fit" ? "fit" : viewEnd} onChange={(t) => setView(String(t))} />
			{!inModal && <OpenEditorButton onOpen={() => setEditing(true)} readOnly={isFormula || baselineFixed || !canEdit} />}
		</div>
	);
	const chart = (height: number, editable: boolean) => (
		<RangeEditor
			node={shown}
			range={range}
			plan={plan}
			onChange={setDraft}
			onCommit={(r) => {
				setDraft(null);
				saveActive(r);
			}}
			implied={implied}
			tone={baselineFixed ? "muted" : inScenario && !isFormula ? "scenario" : "baseline"}
			focus={inScenario ? "result" : "values"}
			statedLabel={ownValues ? scenario!.name : "Baseline"}
			resultLabel={inScenario ? `${scenario!.name}, with risks` : undefined}
			ghost={inScenario ? (baseQ?.[2] ?? null) : null}
			ghostLabel="Without scenario"
			readOnly={isFormula || baselineFixed || !editable || !canEdit}
			height={height}
			viewEnd={viewEnd}
			viewStart={viewStart}
			labels={labels}
			legend
		/>
	);

	const low = implied ? implied[0][ht] : null;
	const likely = implied ? implied[2][ht] : null;
	const high = implied ? implied[4][ht] : null;
	// A constant with nothing moving it is one number (A13); no low / likely / high.
	// Only when it is also one value over time; "$150k, then $170k from Jan" still needs its date.
	const plainConstant = isConstant && !hasDrivers && new Set(Object.values(activeRange).map((r) => r[1])).size <= 1;
	const srSummary = implied
		? `${isFormula ? "Calculated" : hasDrivers ? "With risks" : "Baseline"}: ${keyDates
				.map((m) => {
					const t = Math.min(N - 1, idx(m));
					return plainConstant
						? `${fmtValue(implied[2][t], u)} in ${fmtMonth(m)}`
						: `${fmtMonth(m)} likely ${fmtValue(implied[2][t], u)} (low ${fmtValue(implied[0][t], u)}, high ${fmtValue(implied[4][t], u)})`;
				})
				.join(", ")}.`
		: "";

	return (
		<div>
			<NodeHeader
				node={node}
				share={{
					value: fmtValue(likely, u),
					caption: plainConstant ? "constant" : `likely in ${fmtMonth(hero)}`,
					curve: implied ? Array.from(implied[2]).slice(0, Math.max(12, viewEnd + 1)) : null,
				}}
				onForecast={isFormula || !canEdit ? undefined : () => setEditing(true)}
			/>

			<div className="grid gap-x-10 gap-y-12 xl:grid-cols-[minmax(0,1fr)_360px]">
				<div className="min-w-0">
					{/* Hero: the answer first (8.2). No card border; the chart below carries the only border. */}
					<section aria-label="Headline">
						<div className="flex flex-wrap items-end gap-x-10 gap-y-5">
							<div>
								<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
									<span className={HERO_NUMBER} aria-live="polite">
										{implied ? fmtValue(likely, u) : "…"}
									</span>
									{plainConstant ? (
										<span className="rounded-md border px-2 py-0.5 text-sm text-muted-foreground">Constant</span>
									) : (
										<span className="text-xl text-foreground/80">
											likely{analyst && " (p50)"} in <HeroDateSelect value={hero} dates={keyDates} onChange={setHero} format={fmtMonth} />
										</span>
									)}
								</div>
								{!plainConstant && implied && (
									<div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
										<span>
											Low{analyst && " (p10)"} <span className="font-medium text-foreground tabular-nums">{fmtValue(low, u)}</span> · High{analyst && " (p90)"}{" "}
											<span className="font-medium text-foreground tabular-nums">{fmtValue(high, u)}</span>
										</span>
										{hasDrivers && !isFormula && <span>{term("with risks", "implied")}: the baseline adjusted by linked events</span>}
									</div>
								)}
							</div>
							{((plan && hasDrivers && !isFormula) || scenario) && (
								<div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
									{plan && hasDrivers && !isFormula && (
										<Stat
											label={ownValues ? `${scenario!.name}: values` : term("Baseline", "before linked events")}
											value={isConstant ? fmtValue(plan.mid[ht], u) : `${fmtValue(plan.lo[ht], u)} – ${fmtValue(plan.hi[ht], u)}`}
											tone={ownValues ? "scenario" : "baseline"}
										/>
									)}
									{scenario && <Stat label="Without scenario" value={baseQ ? fmtValue(baseQ[2][ht], u) : "…"} tone="ink" pending={pending} />}
								</div>
							)}
						</div>
						{srSummary && <p className="sr-only">{srSummary}</p>}
					</section>

					{isolated && <NextStepCallout node={node} />}

					<div className="mt-10 mb-3">
						<ScenarioBar nodeIds={[node.id, ...ancestorIds]} />
					</div>
					{scenario && <NodeScenarioStrip node={node} scenario={scenario} />}

					{/* Chart: drag in place; "Open editor" for the full-screen editor and its table. */}
					<div className="rounded-xl border bg-card p-4 pt-3">
						<div className="mb-2 flex flex-wrap items-center gap-3">{toolbar(false)}</div>
						<ChartPreview onOpen={() => setEditing(true)} readOnly={isFormula || baselineFixed || !canEdit} openButton={false}>
							{chart(previewHeight, true)}
						</ChartPreview>
					</div>

					{isFormula ? (
						<div className="mt-10 border-t pt-6">
							<div className="mb-4 flex items-start gap-2 text-sm">
								<LockKeyhole className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
								<p>
									This is calculated. To change it, drag one of its drivers:{" "}
									{drivers(node.id, nodes, links).map((id, i) => (
										<span key={id}>
											{i > 0 && ", "}
											<Link className="font-medium hover:underline" to={`/node/${id}`}>
												<NodeIcon node={nodes[id]} className="mr-1 text-muted-foreground" />
												{nodes[id].title}
											</Link>
										</span>
									))}
									.
								</p>
							</div>
							<Textarea
								className="font-mono text-sm"
								value={formula ?? node.formula}
								onChange={(e) => setFormula(e.target.value)}
								rows={2}
								aria-label="Formula"
							/>
							<div className="mt-2 flex items-center gap-3">
								<p className={cn("flex-1 text-xs", fErr ? "text-[var(--risk-ink)]" : "text-muted-foreground")}>
									{fErr ?? "Formula names, + − × ÷, min, max, floor, ceil, round, sum, lag(metric, months), happened(event)."}
								</p>
								{formula !== null && formula !== node.formula && (
									<>
										<Button size="sm" variant="ghost" onClick={() => setFormula(null)}>
											Cancel
										</Button>
										<Button
											size="sm"
											disabled={!!fErr || checking}
											onClick={() => {
												void saveFormula(node.id, formula).then((ok) => ok && setFormula(null));
											}}
										>
											Save formula
										</Button>
									</>
								)}
							</div>
						</div>
					) : (
						<div className="mt-8">{rangeTable()}</div>
					)}
				</div>

				<aside className="min-w-0" aria-label="Drivers and details">
					<LinksPanel node={node} />
					<Section title={`${term("What matters most", "tornado sensitivity")} in ${fmtMonth(hero)}`}>
						<WhatMattersMost nodeId={node.id} date={hero} upstream={ancestorIds.length} />
					</Section>
					{!isFormula && (
						<Section title="Track it">
							<p className="mb-3 text-sm text-muted-foreground">
								Log what actually happened. Observations after {fmtMonth(NOW)} pin every simulated future to the real value from that month.
							</p>
							<div className="flex flex-wrap items-end gap-2">
								<div className="space-y-1">
									<Label htmlFor={`obs-month-${node.id}`} className="text-xs text-muted-foreground">
										Month
									</Label>
									<Input id={`obs-month-${node.id}`} type="month" className="h-8 w-40" value={obsMonth} onChange={(e) => setObsMonth(e.target.value)} />
								</div>
								<div className="space-y-1">
									<Label htmlFor={`obs-value-${node.id}`} className="text-xs text-muted-foreground">
										Value{u === "%" ? " (%)" : u === "USD" ? " ($)" : u ? ` (${u})` : ""}
									</Label>
									<Input
										id={`obs-value-${node.id}`}
										className="h-8 w-36 tabular-nums"
										inputMode="decimal"
										placeholder={u === "%" ? "5%" : u === "USD" ? "$2.5M" : u === "people" ? "40" : "150k"}
										value={obsValue}
										onChange={(e) => setObsValue(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter" && obsParsed !== null && obsMonth) {
												void addObservation(node.id, obsMonth, obsParsed);
												setObsValue("");
												setObsMonth(addMonths(obsMonth, 1));
											}
										}}
									/>
								</div>
								<Button
									size="sm"
									variant="outline"
									disabled={obsParsed === null || !obsMonth}
									onClick={() => {
										if (obsParsed === null) return;
										void addObservation(node.id, obsMonth, obsParsed);
										setObsValue("");
										setObsMonth(addMonths(obsMonth, 1));
									}}
								>
									Log observation
								</Button>
							</div>
							<ul className="mt-3 max-h-56 space-y-0.5 overflow-y-auto text-sm tabular-nums">
								{[...node.observed].reverse().map((o) => (
									<li
										key={o.month}
										className={cn("group flex items-center justify-between rounded px-1.5 py-0.5 hover:bg-muted", idx(o.month) >= 0 && "font-medium")}
									>
										<span className="text-muted-foreground">{fmtMonth(o.month)}</span>
										<span className="flex items-center gap-1">
											{fmtValue(o.value, u)}
											<button
												className="opacity-0 group-hover:opacity-100 focus:opacity-100"
												onClick={() => void removeObservation(node.id, o.month)}
												aria-label={`Remove ${fmtMonth(o.month)}`}
											>
												<X className="size-3.5" />
											</button>
										</span>
									</li>
								))}
							</ul>
						</Section>
					)}
					<AdminGroup>
						<HistoryPanel node={node} />
					</AdminGroup>
				</aside>
			</div>

			<ChartModal
				open={editing}
				onOpenChange={(v) => {
					setEditing(v);
					setDraft(null);
				}}
				title={node.title}
				description={
					isFormula
						? "The calculated range, full screen."
						: baselineFixed
							? `${scenario!.name} uses the baseline values here. Give it its own values to change them.`
							: `Edit the ${ownValues ? `${scenario!.name} values` : "baseline range"} by dragging the chart or typing in the table.`
				}
				stats={
					implied ? (
						<>
							<span className="text-[var(--model)]">
								{isFormula ? "Calculated" : "With risks"}, {labels.likely.toLowerCase()} <strong className="tabular-nums">{fmtValue(implied[2][ht], u)}</strong>
							</span>
							<span className="text-muted-foreground">at {fmtMonth(hero)}</span>
						</>
					) : null
				}
			>
				<div className="mb-2 flex flex-wrap items-center gap-3">{toolbar(true)}</div>
				<div className="rounded-xl border bg-card p-4">{chart(modalHeight, true)}</div>
				{!isFormula && !baselineFixed && (
					<p className="mt-2 text-xs text-muted-foreground">
						History is fixed. Drag the {labels.likely.toLowerCase()} point to move the baseline; drag the {labels.low.toLowerCase()} and{" "}
						{labels.high.toLowerCase()} points to widen or narrow it. Click the chart to add a date; drag a {labels.likely.toLowerCase()} point off the chart to
						remove it. The baseline is before surprises; “with risks” adds the linked events.
					</p>
				)}
				{!isFormula && <div className="mt-6 max-w-4xl">{rangeTable()}</div>}
			</ChartModal>
		</div>
	);
}
